/* eslint-env es2020 */
// PLAYTEST MODE (Matt, 2026-09-25): "turn it on for a game, have each
// player's microphone dictate what they are saying, have you parse those
// things in real time and be working on fixes while we play."
//
// Any seated player switches it on for the room (`commonwealthPlaytest/
// <gameId>/on`); each phone then turns on ITS OWN microphone with a tap
// (the browser needs the gesture, and nobody is recorded without choosing
// to be). Speech is transcribed on the phone by the Web Speech API and each
// finished sentence is pushed to `commonwealthPlaytest/<gameId>/lines`,
// tagged with who, which roles, and where the game was. Only the admin CLI
// reads it back: `yarn playtest:watch`, which Claude tails while you play.
import { ref as dbRef, onValue, set, push, serverTimestamp, query, orderByChild, limitToLast } from 'firebase/database';
import { reactive } from 'vue';
import { db } from '@/firebase';

const ROOT = 'commonwealthPlaytest';
const Recognition = () => globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition;
export const micSupported = () => Boolean(Recognition());

export const watchPlaytestOn = (gameId, cb) => {
  if (!db || !gameId) return () => {};
  return onValue(dbRef(db, `${ROOT}/${gameId}/on`), (snap) => cb(snap.val() === true), () => cb(false));
};
export const setPlaytestOn = (gameId, on) => (db && gameId ? set(dbRef(db, `${ROOT}/${gameId}/on`), Boolean(on)) : Promise.resolve());

export const sendLine = (gameId, line) => {
  if (!db || !gameId || !line.text?.trim()) return Promise.resolve();
  return push(dbRef(db, `${ROOT}/${gameId}/lines`), { ...line, text: line.text.trim().slice(0, 2000), at: serverTimestamp() }).catch(() => {});
};

/**
 * Keep listening until stopped. Web Speech on iOS ends itself after a
 * silence, so the recognizer is restarted whenever it ends; each FINAL
 * result goes to onLine once. Returns stop().
 */
export const startMic = ({ onLine, onState }) => {
  const R = Recognition();
  if (!R) { onState?.('unsupported'); return () => {}; }
  let stopped = false;
  let rec = null;
  const begin = () => {
    rec = new R();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = navigator.language || 'en-US';
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (r.isFinal && r[0]?.transcript?.trim()) onLine(r[0].transcript.trim());
      }
    };
    rec.onerror = (e) => {
      // iOS refuses a restart that no tap asked for: that is a PAUSE, and
      // the next tap anywhere picks it back up (see armResume).
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') { stopped = true; onState?.('paused'); }
    };
    rec.onend = () => { if (!stopped) setTimeout(() => { if (!stopped) { try { begin(); } catch { onState?.('paused'); } } }, 250); };
    try { rec.start(); onState?.('on'); } catch { onState?.('paused'); }
  };
  begin();
  return () => { stopped = true; try { rec?.stop(); } catch { /* already stopped */ } onState?.('off'); };
};

// ------------------------------------------------------------ one per phone

export const playtest = reactive({ gameId: null, on: false, mic: 'off', sent: 0, note: null, listener: null, now: Date.now() });

// THE LISTENER'S HEARTBEAT (2026-09-28): the playtest worker on the Mac
// writes `listener` { at, lines, lastReply, state } every 20s. The slip on
// every screen reads it, so the table always knows whether Claude is
// listening - and that what they say is saved for the audit either way.
let offListener = null; let clockTimer = null;
const watchListener = (gameId) => {
  offListener?.(); offListener = null;
  clearInterval(clockTimer); clockTimer = null;
  playtest.listener = null;
  if (!db || !gameId) return;
  offListener = onValue(dbRef(db, `${ROOT}/${gameId}/listener`), (snap) => { playtest.listener = snap.val() || null; }, () => {});
  clockTimer = setInterval(() => { playtest.now = Date.now(); }, 5000);
};
export const STALE_MS = 75000;
/** What the slip says: { tone: 'ok'|'stale'|'off', text }. */
export const listenerStatus = () => {
  const l = playtest.listener;
  const ago = (t) => { const s = Math.max(0, Math.round((playtest.now - t) / 1000)); return s < 60 ? `${s}s ago` : `${Math.round(s / 60)} min ago`; };
  if (!l?.at) return { tone: 'off', text: 'Claude isn’t listening yet - what you say is still saved for the audit' };
  if (l.state === 'done') return { tone: 'ok', text: 'Playtest written up' };
  if (playtest.now - l.at > STALE_MS) return { tone: 'stale', text: `Claude isn’t listening right now (last seen ${ago(l.at)}) - what you say is still saved for the audit` };
  const word = { thinking: 'reading', fixing: 'fixing', 'writing up': 'writing up' }[l.state] || 'listening';
  return { tone: 'ok', text: `Claude ${word} · ${l.lines || 0} lines${l.lastReply ? ` · replied ${ago(l.lastReply)}` : ''}` };
};

// Claude's replies (`yarn playtest:say`), so the table SEES it is being
// heard (playtest 8: "you say you're listening but you don't hear anything
// I'm saying"). Only notes newer than the page are shown, for 25 seconds.
// They QUEUE (playtest 12, Matt: "the note went way too quickly"): replies
// land several at once, and only the newest was watched, so each wiped the
// last within a second. Now each gets NOTE_MIN_MS when more are waiting, and
// the last stays NOTE_MS.
const NOTE_MS = 25000;
const NOTE_MIN_MS = 12000;
let offNotes = null; let noteTimer = null;
const noteQueue = [];
let lastQueued = 0;
const showNext = () => {
  clearTimeout(noteTimer);
  const n = noteQueue.shift();
  if (!n) { playtest.note = null; return; }
  playtest.note = n;
  noteTimer = setTimeout(showNext, noteQueue.length ? NOTE_MIN_MS : NOTE_MS);
};
const watchNotes = (gameId) => {
  offNotes?.(); offNotes = null;
  noteQueue.length = 0;
  if (!db || !gameId) return;
  const since = Date.now() - 60000;
  offNotes = onValue(query(dbRef(db, `${ROOT}/${gameId}/notes`), orderByChild('at'), limitToLast(6)), (snap) => {
    const fresh = Object.values(snap.val() || {})
      .filter((n) => n?.text && !n.quiet && (n.at || 0) >= since && (n.at || 0) > lastQueued)
      .sort((a, b) => a.at - b.at);
    if (!fresh.length) return;
    fresh.forEach((n) => noteQueue.push({ text: n.text, at: n.at }));
    lastQueued = fresh[fresh.length - 1].at;
    if (!playtest.note) showNext();
    else if (noteTimer && noteQueue.length === fresh.length) {
      // Something is up and was going to stay the long time: cut it to the
      // short time now that something is waiting behind it.
      clearTimeout(noteTimer);
      noteTimer = setTimeout(showNext, NOTE_MIN_MS);
    }
  }, () => {});
};
let offWatch = null;
let stopMic = null;
let contextFn = () => ({});
// Whether THIS phone wants its mic on, remembered across reloads (playtest
// 2026-09-25: after the dawn reload the mic was silently off and "Claude's
// not listening"). The browser still needs a tap to start it, so the next
// tap anywhere on the screen does. Remembered PER GAME: one flag for the
// whole phone meant a mic switched on in an old game started itself on the
// first tap in a new one, without anyone choosing it (playtest, 2026-10-20).
const wantKey = () => `commonwealth:mic-wanted:${playtest.gameId}`;
const wanted = () => { try { return Boolean(playtest.gameId) && localStorage.getItem(wantKey()) === '1'; } catch { return false; } };
const setWanted = (on) => { try { if (playtest.gameId) localStorage.setItem(wantKey(), on ? '1' : '0'); } catch { /* storage off */ } };

/** App.vue says who and where each line comes from. */
export const setMicContext = (fn) => { contextFn = fn; };

const begin = () => {
  if (stopMic || !playtest.gameId || !playtest.on) return;
  stopMic = startMic({
    onLine: (text) => { playtest.sent += 1; sendLine(playtest.gameId, { ...contextFn(), text }); },
    onState: (st) => {
      playtest.mic = st;
      if (st === 'paused' || st === 'unsupported') { stopMic = null; if (st === 'paused') armResume(); }
    },
  });
};

// The next tap anywhere restarts a mic that should be on. On the END of
// the tap, not its start: iOS grants the gesture to touchend and click but
// not to pointerdown, so a restart from pointerdown was refused, paused
// again and re-armed, and the mic stayed paused through every tap on the
// board until the pill itself was pressed (playtest 2026-10-20, Matt:
// "my mic paused again... this is annoying").
let armed = false;
const RESUME_ON = ['touchend', 'click'];
const armResume = () => {
  if (armed || typeof document === 'undefined') return;
  armed = true;
  const go = (e) => {
    RESUME_ON.forEach((t) => document.removeEventListener(t, go, true));
    armed = false;
    // A tap on the pill is the pill's own toggle: starting here too would
    // have its click switch the mic straight back off.
    if (e?.target?.closest?.('.playtest-pill')) return;
    if (wanted() && playtest.on) begin();
  };
  RESUME_ON.forEach((t) => document.addEventListener(t, go, true));
};

/** Follow a room's playtest switch (App.vue calls this as rooms change). */
export const bindPlaytest = (gameId) => {
  if (playtest.gameId === gameId) return;
  offWatch?.(); offWatch = null;
  watchNotes(null);
  watchListener(null);
  stopMic?.(); stopMic = null;
  Object.assign(playtest, { gameId, on: false, mic: 'off', sent: 0 });
  if (gameId) offWatch = watchPlaytestOn(gameId, (on) => {
    playtest.on = on;
    watchNotes(on ? gameId : null);
    watchListener(on ? gameId : null);
    if (!on && stopMic) { stopMic(); stopMic = null; }
    if (on && wanted() && !stopMic) { playtest.mic = 'paused'; armResume(); }
  });
};

/** Turn this phone's microphone on or off (a tap, so the browser allows it). */
export const toggleMic = (context) => {
  if (context) contextFn = context;
  if (stopMic) { setWanted(false); stopMic(); stopMic = null; playtest.mic = 'off'; return; }
  setWanted(true);
  begin();
};
